"use client";

import { CheckCircle2, CircleAlert, Clock3, Link2, Loader2, Save, UserPlus, UserRound } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Dialog } from "@nitro/ui";
import { io } from "socket.io-client";
import { API_URL, apiFetch } from "../lib/api";
import {
  getRegistrationAvailability,
  getRegistrationPresentation,
  registrationEntryLabel,
  registrationFormForState,
  isUnknownCompetitionRegistrationOutcome,
  restoreCompetitionRegistrationOutcome,
  runCompetitionRegistrationOutcome,
  runRegistrationSingleFlight,
  scheduleCancellableMicrotask,
} from "../lib/competitionRegistration";
import styles from "./CompetitionRegistrationModal.module.css";

const DEFAULT_ADAPTERS = Object.freeze({
  apiUrl: API_URL,
  openConnector: openConnectorPopup,
  request: apiFetch,
  socketFactory: io,
});

export function CompetitionRegistrationButton({ adapters = DEFAULT_ADAPTERS, className = "nitro-primary-button", competition }) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [registrationState, setRegistrationState] = useState(null);
  const buttonLabel = registrationButtonLabel(competition, registrationState);
  const registrationAvailability = getRegistrationAvailability(competition);
  const registrationPresentation = getRegistrationPresentation(competition);
  const isRegistrationOpen = registrationAvailability.canRegister;
  const registrationHref = registrationEntryHref(pathname, competition);

  function closeModal() {
    setOpen(false);
    if (shouldRefresh) {
      setShouldRefresh(false);
      if (typeof adapters?.refresh === "function") adapters.refresh();
      else router.refresh();
    }
  }

  if (registrationPresentation.managedParticipation) return null;

  if (!isRegistrationOpen) {
    return (
      <button
        className={className}
        disabled
        style={{ minHeight: 44 }}
        title={registrationAvailability.reason || registrationPresentation.statusLabel}
        type="button"
      >
        <Clock3 size={16} />
        {registrationPresentation.statusLabel || buttonLabel}
      </button>
    );
  }

  return (
    <>
      <button className={className} onClick={() => setOpen(true)} style={{ minHeight: 44 }} type="button">
        <UserPlus size={16} />
        {buttonLabel}
      </button>
      {open ? (
        <CompetitionRegistrationModal
          adapters={adapters}
          competition={competition}
          onClose={closeModal}
          onLoaded={(data) => {
            if (data?.status) setRegistrationState(data);
          }}
          onRegistered={(data) => {
            if (data?.status) setRegistrationState(data);
            setShouldRefresh(true);
          }}
          registrationHref={registrationHref}
        />
      ) : null}
    </>
  );
}

export function CompetitionRegistrationModal({ adapters = DEFAULT_ADAPTERS, competition, onClose, onLoaded, onRegistered, registrationHref }) {
  const { apiUrl = API_URL, openConnector = openConnectorPopup, outcomeOptions, request = apiFetch, socketFactory = io } = adapters || {};
  const [state, setState] = useState({ loading: true, error: "", data: null, authRequired: false, authenticated: false });
  const [accountValue, setAccountValue] = useState("");
  const [registerForm, setRegisterForm] = useState({ carId: "", carClassId: "", gameAccountId: "", skinId: "", raceNumber: "" });
  const [busy, setBusy] = useState(false);
  const [liveNotice, setLiveNotice] = useState("");
  const loadSequenceRef = useRef(0);
  const loadAbortRef = useRef(null);
  const connectorPopupRef = useRef(null);
  const actionPromiseRef = useRef(null);
  const uncertainAlertRef = useRef(null);
  const onLoadedRef = useRef(onLoaded);
  const onRegisteredRef = useRef(onRegistered);
  onLoadedRef.current = onLoaded;
  onRegisteredRef.current = onRegistered;

  useEffect(() => {
    if (state.uncertainFocus) uncertainAlertRef.current?.focus();
  }, [state.uncertainFocus]);

  const loadRegistrationState = useCallback(async (silent = false, selectedGameAccountId = "", selectedCarId = "") => {
    const sequence = ++loadSequenceRef.current;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    if (!silent) {
      setState((current) => ({ ...current, loading: true, error: "", authRequired: false }));
    }
    try {
      const params = new URLSearchParams();
      if (selectedGameAccountId) params.set("gameAccountId", selectedGameAccountId);
      if (selectedCarId) params.set("carId", selectedCarId);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      const data = await request(`/api/v1/competitions/${encodeURIComponent(competition.slug)}/registration${suffix}`, {
        cache: "no-store",
        signal: controller.signal,
        timeoutMs: 30000,
      });
      if (sequence !== loadSequenceRef.current || controller.signal.aborted) return;
      const uncertain = await restoreCompetitionRegistrationOutcome({
        competitionId: data?.competition?.id,
        destination: apiUrl,
        expectedCompetitionId: competition.id,
        outcomeOptions,
        request,
        signal: controller.signal,
      });
      if (sequence !== loadSequenceRef.current || controller.signal.aborted) return;
      setState({ loading: false, error: "", data, authRequired: false, authenticated: true, uncertain, uncertainFocus: 0 });
      setRegisterForm((current) => registrationFormForState(data, current, { selectedGameAccountId, selectedCarId }));
      onLoadedRef.current?.(data);
    } catch (error) {
      if (sequence !== loadSequenceRef.current || controller.signal.aborted || error?.name === "AbortError") return;
      setState((current) => ({
        loading: false,
        error: error?.status === 401 ? "" : error?.message || "No fue posible cargar la inscripción.",
        data: current.data,
        authRequired: error?.status === 401,
        authenticated: false,
      }));
    }
  }, [apiUrl, competition.id, competition.slug, outcomeOptions, request]);

  useEffect(() => {
    const cancelInitialLoad = scheduleCancellableMicrotask(() => loadRegistrationState());
    const allowedOrigins = new Set([window.location.origin]);
    try {
      allowedOrigins.add(new URL(apiUrl).origin);
    } catch {
      // La carga normal mostrará el error de configuración sin ampliar orígenes de mensajes.
    }
    function onMessage(event) {
      if (!connectorPopupRef.current || event.source !== connectorPopupRef.current) return;
      if (!allowedOrigins.has(event.origin)) return;
      if (
        event.data?.source === "nitro-v4"
        && ["nitro_iracing_link", "nitro_steam_link", "nitro_social_login"].includes(event.data?.type)
      ) {
        connectorPopupRef.current = null;
        loadRegistrationState();
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      cancelInitialLoad();
      ++loadSequenceRef.current;
      loadAbortRef.current?.abort();
      window.removeEventListener("message", onMessage);
    };
  }, [apiUrl, loadRegistrationState]);

  useEffect(() => {
    if (!state.authenticated || !state.data || typeof socketFactory !== "function") return undefined;
    const socket = socketFactory(apiUrl, {
      withCredentials: true,
      transports: ["websocket"],
    });
    if (!socket?.on || !socket?.emit || !socket?.disconnect) return undefined;

    socket.on("connect", () => {
      socket.emit("competition:join", { competitionSlug: competition.slug });
    });

    socket.on("competition:registration-updated", (payload) => {
      if (payload?.competition?.slug !== competition.slug) return;
      setLiveNotice("Cupos actualizados en tiempo real.");
      setState((current) => {
        if (!current.data) return current;
        const isFull = !!payload.capacity?.isFull;
        return {
          ...current,
          data: {
            ...current.data,
            capacity: payload.capacity || current.data.capacity,
            status: isFull && current.data.status.step === "ready"
              ? {
                  ...current.data.status,
                  canRegister: false,
                  step: "full",
                  reason: "Los cupos estan completos.",
                  cta: "Cupos completos",
                }
              : current.data.status,
          },
        };
      });
    });

    return () => socket.disconnect();
  }, [apiUrl, competition.slug, socketFactory, state.authenticated]);

  const nextRequirement = useMemo(() => {
    return state.data?.eligibility?.requiredAccounts?.find((item) => item.status !== "ready") || null;
  }, [state.data]);

  const selectedCarRequired = (state.data?.availableCars || []).length > 1;

  function connectAccount(requirement) {
    const platform = requirement?.platform;
    if (!platform) return;
    if (platform.slug === "steam") {
      connectorPopupRef.current = openConnector(`${apiUrl}/api/v1/pilot-accounts/connect/steam/start`, "nitroSteamConnect");
      return;
    }
    if (platform.slug === "iracing") {
      connectorPopupRef.current = openConnector(`${apiUrl}/api/v1/iracing/connect/start`, "nitroIracingConnect");
      return;
    }
    if (!accountValue.trim()) {
      setState((current) => ({ ...current, error: `Escribe tu ${platform.valueLabel || platform.name}.` }));
      return;
    }
    return runRegistrationSingleFlight(actionPromiseRef, async () => {
      setBusy(true);
      try {
        await request(`/api/v1/pilot-accounts/connect/${encodeURIComponent(platform.slug)}`, {
          method: "POST",
          body: JSON.stringify({
            accountValue: accountValue.trim(),
            displayValue: accountValue.trim(),
          }),
        });
        setAccountValue("");
        await loadRegistrationState();
      } catch (error) {
        setState((current) => ({
          ...current,
          error: error.message,
          data: error.details?.capacity && current.data ? {
            ...current.data,
            capacity: error.details.capacity,
            status: {
              ...current.data.status,
              canRegister: false,
              step: "full",
              reason: "Los cupos estan completos.",
              cta: "Cupos completos",
            },
          } : current.data,
        }));
      } finally {
        setBusy(false);
      }
    });
  }

  function confirmRegistration() {
    return runRegistrationSingleFlight(actionPromiseRef, async () => {
      setBusy(true);
      setState((current) => ({ ...current, error: "" }));
      try {
        const data = await runCompetitionRegistrationOutcome({
          competitionId: state.data?.competition?.id,
          destination: apiUrl,
          expectedCompetitionId: competition.id,
          outcomeOptions,
          request,
          execute: () => request(`/api/v1/competitions/${encodeURIComponent(competition.slug)}/register`, {
            method: "POST",
            body: JSON.stringify(registerForm),
          }),
        });
        setState({ loading: false, error: "", data, authRequired: false, authenticated: true });
        onRegisteredRef.current?.(data);
      } catch (error) {
        if (isUnknownCompetitionRegistrationOutcome(error)) {
          setState((current) => ({ ...current, error: "", uncertain: true, uncertainFocus: Date.now() }));
        } else {
          setState((current) => ({ ...current, error: error.message }));
        }
      } finally {
        setBusy(false);
      }
    });
  }

  const dialogDescription = [
    competition.typeLabel || "Competencia",
    state.data?.competition?.simulator?.name || competition.game || competition.simulator?.name,
  ].filter(Boolean).join(" · ");

  return (
    <Dialog
      bodyClassName={styles.body}
      className={styles.dialog}
      closeDisabled={busy}
      density="compact"
      description={dialogDescription}
      dismissible={!busy}
      dismissOnBackdrop={false}
      onClose={onClose}
      open
      size="md"
      title={competition.title}
    >
      {state.loading ? (
        <div className="registration-loading"><Loader2 size={22} /> Cargando inscripcion...</div>
      ) : null}

      {!state.loading && state.authRequired ? (
        <AuthRedirectStep registrationHref={registrationHref} />
      ) : null}

      {!state.loading && state.error ? (
        <Alert
          actions={<Button disabled={busy} onClick={() => loadRegistrationState()} size="sm" variant="secondary">Reintentar</Button>}
          className={styles.alert}
          tone="danger"
          title="No pudimos cargar la inscripción"
        >
          {state.error}
        </Alert>
      ) : null}

      {state.data ? (
        <div className="registration-flow">
          <RegistrationSummary data={state.data} />
          {liveNotice ? <Alert className={styles.alert} tone="warning">{liveNotice}</Alert> : null}
          <RegistrationChecklist data={state.data} />

          {state.data.status.step === "profile" && !state.uncertain ? (
            <div className="registration-action-panel">
              <CircleAlert size={18} />
              <div>
                <strong>Falta completar tu perfil</strong>
                <p>Campos pendientes: {state.data.profile.missingFields.join(", ")}.</p>
                <a className="nitro-primary-button compact" href="/perfil"><UserRound size={15} /> Completar perfil</a>
              </div>
            </div>
          ) : null}

          {["account", "account_pending"].includes(state.data.status.step) && !state.uncertain ? (
            <AccountStep
              accountValue={accountValue}
              busy={busy}
              onAccountValue={setAccountValue}
              onConnect={connectAccount}
              requirement={nextRequirement}
            />
          ) : null}

          {state.uncertain ? (
            <Alert
              actions={<Button onClick={onClose} size="sm" variant="secondary">Cerrar</Button>}
              className={styles.alert}
              ref={uncertainAlertRef}
              tabIndex={-1}
              title="No pudimos confirmar la inscripción"
              tone="danger"
            >
              <span className="nitro-chip nitro-chip-red">Resultado por conciliar</span>
              <p>La inscripción podría haberse registrado. No vuelvas a enviarla. Contacta a la administración de la competencia para que confirme el estado.</p>
            </Alert>
          ) : null}

          {state.data.status.step === "ready" && !state.uncertain ? (
            <ReadyStep
              busy={busy}
              data={state.data}
              form={registerForm}
              loading={state.loading}
              onAccountChange={(gameAccountId) => {
                const resetsDetectedNumber = (state.data?.availableCategories || [])
                  .some((category) => category.categoryType === "driver_class");
                setRegisterForm((current) => ({
                  ...current,
                  gameAccountId,
                  raceNumber: resetsDetectedNumber ? "" : current.raceNumber,
                }));
                loadRegistrationState(false, gameAccountId, registerForm.carId);
              }}
              onCarChange={(carId) => {
                setRegisterForm((current) => ({ ...current, carId, carClassId: "", skinId: "", raceNumber: "" }));
                loadRegistrationState(false, registerForm.gameAccountId, carId);
              }}
              onChange={setRegisterForm}
              onConfirm={confirmRegistration}
              selectedCarRequired={selectedCarRequired}
            />
          ) : null}

          {state.data.status.step === "registered" && !state.uncertain ? (
            <div className="registration-success-panel">
              <CheckCircle2 size={22} />
              <div>
                <strong>{state.data.status.reason}</strong>
                <p>La reglamentacion queda aceptada con tu inscripcion.</p>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function AuthRedirectStep({ registrationHref }) {
  return (
    <div className="registration-action-panel">
      <UserPlus size={18} />
      <div>
        <strong>Entra para inscribirte</strong>
        <p>Crea tu piloto o inicia sesion. Al terminar vuelves a este evento.</p>
        <a className="nitro-primary-button compact" href={registrationHref}>
          <UserPlus size={15} />
          Iniciar sesion / Registro
        </a>
      </div>
    </div>
  );
}

function RegistrationSummary({ data }) {
  const detection = data.driverClassDetection;
  return (
    <div className="registration-summary">
      <span>{data.competition.simulator?.name}</span>
      <strong>{detection?.status === "ready" ? `${detection.category.name} / iRating ${Math.trunc(detection.rating)}` : data.capacity.label}</strong>
      <em>{detection?.status === "ready" ? "Categoria detectada segun reglamento" : data.status.reason}</em>
    </div>
  );
}

function RegistrationChecklist({ data }) {
  const checks = [
    { label: "Sesion iniciada", ok: true },
    { label: "Perfil minimo", ok: data.profile.complete },
    { label: "Cuenta del simulador", ok: !!data.eligibility?.canRace },
    { label: data.capacity.max ? "Cupos disponibles" : "Inscripcion abierta", ok: !data.capacity.isFull },
  ];
  return (
    <div className="registration-checklist">
      {checks.map((check) => (
        <span className={check.ok ? "is-ok" : "is-pending"} key={check.label}>
          {check.ok ? <CheckCircle2 size={15} /> : <CircleAlert size={15} />}
          {check.label}
        </span>
      ))}
    </div>
  );
}

function AccountStep({ accountValue, busy, onAccountValue, onConnect, requirement }) {
  const platform = requirement?.platform;
  if (!platform) return null;
  if (requirement.status === "identity_conflict") {
    return (
      <div className="registration-action-panel">
        <CircleAlert size={18} />
        <div>
          <strong>Identidad {platform.name} en conflicto</strong>
          <p>Esta identidad aparece activa para mas de un piloto. Ninguna de esas cuentas puede inscribirse hasta que un administrador resuelva los datos.</p>
        </div>
      </div>
    );
  }
  const oauth = ["oauth", "steam_openid"].includes(platform.connectorType) || ["steam", "iracing"].includes(platform.slug);
  return (
    <div className="registration-action-panel">
      <UserPlus size={18} />
      <div>
        <strong>Conecta {platform.name}</strong>
        <p>{requirement.instructions || platform.valueHelp || "Esta cuenta es necesaria para inscribirte en este simulador."}</p>
        {!oauth ? (
          <input
            placeholder={platform.valuePlaceholder || platform.valueLabel || platform.name}
            value={accountValue}
            onChange={(event) => onAccountValue(event.target.value)}
          />
        ) : null}
        <button className="nitro-primary-button compact" disabled={busy} onClick={() => onConnect(requirement)} type="button">
          {oauth ? <Link2 size={15} /> : <Save size={15} />}
          {oauth ? `Conectar con ${platform.name}` : "Guardar cuenta"}
        </button>
      </div>
    </div>
  );
}

function ReadyStep({ busy, data, form, loading, onAccountChange, onCarChange, onChange, onConfirm, selectedCarRequired }) {
  const carClassCategories = (data.availableCategories || []).filter((category) => category.categoryType === "car_class");
  const driverClassCategories = (data.availableCategories || []).filter((category) => category.categoryType === "driver_class");
  const gameAccounts = registrationGameAccounts(data);
  const requirements = data.registrationRequirements || {};
  const shouldSelectCar = requirements.car?.mode === "required";
  const shouldSelectCarClass = requirements.category?.mode === "required";
  const shouldSelectSkin = requirements.skin?.mode !== "disabled";
  const skinRequired = requirements.skin?.mode === "required";
  const raceNumberMode = requirements.raceNumber?.mode || "optional";
  const raceNumberRequired = ["required", "derived_from_skin"].includes(raceNumberMode);
  const accountPlatformName = data.eligibility?.requiredAccounts?.find((item) => item.status === "ready")?.platform?.name || "simulador";
  const detection = data.driverClassDetection;
  const detectedCategory = detection?.status === "ready" ? detection.category : null;
  const availableNumbers = Array.isArray(detection?.availableNumbers) ? detection.availableNumbers : [];
  const canSubmit = !busy && !loading
    && (!gameAccounts.length || !!form.gameAccountId)
    && (!selectedCarRequired || !!form.carId)
    && (!shouldSelectCarClass || !!form.carClassId)
    && (!skinRequired || !!form.skinId)
    && (!raceNumberRequired || !!String(form.raceNumber || "").trim())
    && (!driverClassCategories.length || (detection?.status === "ready" && !!String(form.raceNumber || "").trim()));

  return (
    <div className="registration-ready-step">
      {gameAccounts.length > 1 ? (
        <label>
          Cuenta {accountPlatformName}
          <select required value={form.gameAccountId} onChange={(event) => onAccountChange(event.target.value)}>
            <option value="">Selecciona cuenta</option>
            {gameAccounts.map((account, index) => (
              <option key={account.id} value={account.id}>
                {account.displayValue || `Cuenta ${accountPlatformName} ${index + 1}`}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {shouldSelectCar ? (
        <label>
          Auto
          <select required={selectedCarRequired} value={form.carId} onChange={(event) => onCarChange(event.target.value)}>
            <option value="">Selecciona auto</option>
            {data.availableCars.map((car) => <option key={car.id} value={car.id}>{car.name}</option>)}
          </select>
        </label>
      ) : null}
      {shouldSelectCarClass ? (
        <label>
          Categoria de auto
          <select required value={form.carClassId} onChange={(event) => onChange({ ...form, carClassId: event.target.value })}>
            <option value="">Selecciona categoria</option>
            {carClassCategories.map((category) => (
              <option key={category.id} value={category.carClassId || category.id}>{category.name}</option>
            ))}
          </select>
        </label>
      ) : null}
      {shouldSelectSkin ? (
        <label>
          Skin
          <select
            disabled={!form.carId || !(data.availableSkins || []).length}
            required={skinRequired}
            value={form.skinId}
            onChange={(event) => {
              const skinId = event.target.value;
              const skin = (data.availableSkins || []).find((item) => String(item.id) === skinId);
              onChange({
                ...form,
                skinId,
                raceNumber: raceNumberMode === "derived_from_skin" ? skin?.raceNumber || "" : form.raceNumber,
              });
            }}
          >
            <option value="">{(data.availableSkins || []).length ? "Selecciona skin" : "Selecciona primero un auto"}</option>
            {(data.availableSkins || []).map((skin) => (
              <option key={skin.id} value={skin.id}>
                {skin.name}{skin.raceNumberValid ? ` · #${skin.raceNumber}` : ""}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {driverClassCategories.length ? (
        <div className="registration-detected-card">
          <span>Categoria detectada</span>
          {detectedCategory ? (
            <>
              <strong>{detectedCategory.name}</strong>
              <p>iRating usado: {Math.trunc(detection.rating)}. Regla: {categoryRuleText(detectedCategory)}.</p>
            </>
          ) : (
            <>
              <strong>Pendiente</strong>
              <p>{detection?.message || "Necesitamos el historial de iRating para asignar tu categoria."}</p>
            </>
          )}
        </div>
      ) : null}
      {raceNumberMode !== "disabled" ? <label>
        Numero de piloto
        {driverClassCategories.length ? (
          <select disabled={!detectedCategory || !availableNumbers.length} required value={form.raceNumber} onChange={(event) => onChange({ ...form, raceNumber: event.target.value })}>
            <option value="">{availableNumbers.length ? "Selecciona numero disponible" : "Sin numeros disponibles"}</option>
            {availableNumbers.map((number) => <option key={number} value={number}>{number}</option>)}
          </select>
        ) : raceNumberMode === "derived_from_skin" ? (
          <input disabled placeholder="Se deriva de la skin" value={form.raceNumber} />
        ) : (
          <input
            inputMode="numeric"
            maxLength={3}
            placeholder={raceNumberMode === "required" ? "Obligatorio" : "Opcional"}
            required={raceNumberMode === "required"}
            value={form.raceNumber}
            onChange={(event) => onChange({ ...form, raceNumber: event.target.value.replace(/\D/g, "").slice(0, 3) })}
          />
        )}
      </label> : null}
      <p className="registration-terms">Al confirmar aceptas el reglamento, condiciones e instrucciones de la competencia.</p>
      <button className="nitro-primary-button" disabled={!canSubmit} onClick={onConfirm} style={{ minHeight: 44 }} type="button">
        {busy ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
        {data.status.cta}
      </button>
    </div>
  );
}

function registrationGameAccounts(data) {
  return (data.eligibility?.requiredAccounts || [])
    .flatMap((requirement) => requirement.accounts || [])
    .filter((account) => account.verificationStatus === "verified" && account.status === "active" && !account.identityConflict);
}

function categoryRuleText(category) {
  const min = category?.minRating;
  const max = category?.maxRating;
  const numberMin = category?.numberMin;
  const numberMax = category?.numberMax;
  const ratingRule = min ? `iRating >= ${min}` : max ? `iRating <= ${max}` : "sin rango iRating";
  const numberRule = numberMin && numberMax ? `numeros ${numberMin}-${numberMax}` : "sin rango de numero";
  return `${ratingRule}, ${numberRule}`;
}

function registrationButtonLabel(competition, registrationState = null) {
  const entryLabel = registrationEntryLabel(registrationState);
  if (entryLabel) return entryLabel;
  const registration = getRegistrationPresentation(competition);
  return registration.ctaLabel || competition.ctaLabel || "Ver evento";
}

function registrationEntryHref(pathname, competition) {
  const returnTo = pathname || `/eventos/${competition.slug || ""}`;
  const params = new URLSearchParams({
    return: returnTo,
    intent: "inscripcion",
  });

  if (competition?.slug) {
    params.set("competition", competition.slug);
  }

  return `/registro?${params.toString()}`;
}

function openConnectorPopup(url, name) {
  return window.open(url, name, "width=720,height=780,noopener=false,noreferrer=false");
}
